/**
 * Centralized widget exports with metadata
 *
 * To add a new widget:
 * 1. Create your widget component
 * 2. Add export here with metadata
 * 3. That's it! The system handles the rest.
 */

import FinancialSnapshot from './widgets/FinancialSnapshot';
import WeatherWidget from './widgets/WeatherWidget';
import StatsWidget from './widgets/StatsWidget';
import NotificationWidget from './widgets/NotificationsWidget';
// import InboxWidget from './widgets/InboxWidget';
import CalendarWidget from './widgets/CalendarWidget';
import TasksWidget from './widgets/TasksWidget';

/**
 * Widget definition helper
 * @param {Object} config - Widget configuration
 * @param {string} config.id - Unique identifier (required)
 * @param {string} config.name - Display name (required)
 * @param {React.Component} config.component - React component (required)
 * @param {string} config.section - 'top' or 'body' (required)
 * @param {boolean} config.defaultVisible - Show by default (default: true)
 * @param {string} config.size - Widget size: 'small', 'medium', 'large', 'full' (default: 'medium')
 * @param {Function} config.permission - Permission check function (optional)
 * @param {Function} config.mapProps - Map dashboard data to component props (optional)
 * @param {Function} config.titleAccessory - Derive an optional title suffix node from dashboard data (optional)
 * @returns {Object} Widget configuration
 */
export const defineWidget = ({
  id,
  name,
  component,
  section,
  defaultVisible = true,
  size = 'medium',
  permission = null,
  path = ['', () => ''],
  mapProps = () => ({}),
  titleAccessory = null,
}) => ({
  id,
  name,
  component,
  section,
  defaultVisible,
  size,
  permission,
  path,
  mapProps,
  titleAccessory,
});

/**
 * Widget definitions
 * Add new widgets here - order determines initial display order
 */
export const widgets = [
  defineWidget({
    id: 'weather',
    name: 'Weather',
    component: WeatherWidget,
    section: 'header',
    mapProps: ({ organization }) => ({ organization }),
  }),

  defineWidget({
    id: 'stats',
    name: 'Statistics',
    component: StatsWidget,
    section: 'top',
    mapProps: ({ projectsLimit, filters, tasksInfo, financialInfo, userInfo, currency }) => ({
      projectsLimit, filters, tasksInfo, financialInfo, userInfo, currency,
    }),
  }),

  defineWidget({
    id: 'financial',
    name: 'Financial Snapshot',
    component: FinancialSnapshot,
    section: 'body',
    permission: (userInfo) =>
      (userInfo.features?.includes('PRO-PLANNING') && userInfo.permissions?.['lead.all']) ||
      userInfo.effectivePermissions?.is_bookkeeper,
    mapProps: ({ userInfo, financialInfo, currency, setCurrency }) => ({
      userInfo, financialInfo, currency, setCurrency,
    }),
    titleAccessory: ({ currency }) => (currency ? `per ${currency}` : null),
    path: ['window', ({ orgId }) => `/app/pro/${orgId}/invoices`],
    size: 'medium',
  }),

  defineWidget({
    id: 'calendar',
    name: '7-Day Calendar',
    component: CalendarWidget,
    section: 'body',
    path: ['history', () => '/app/v2/calendar'],
    permission: (userInfo) => userInfo.effectivePermissions?.['calendar.full-access'],
    size: 'medium',
  }),

  defineWidget({
    id: 'notifications',
    name: 'Unread Notifications',
    component: NotificationWidget,
    section: 'body',
    path: ['toggleNotifications', () => {}],
    size: 'medium',
  }),

  // defineWidget({
  //   id: 'inbox',
  //   name: 'Inbox',
  //   component: InboxWidget,
  //   section: 'body',
  //   defaultVisible: false,
  //   size: 'medium',
  // }),

  defineWidget({
    id: 'tasks',
    name: 'Urgent Tasks',
    component: TasksWidget,
    section: 'body',
    defaultVisible: true,
    path: ['toggleUrgentTasks', () => {}],
    size: 'medium',
    mapProps: ({ tasksInfo, userInfo }) => ({ tasksInfo, userInfo }),
  }),
];

/**
 * Auto-generated default order by section
 */
export const getDefaultOrder = () => {
  const order = { top: [], body: [], header: [] };
  widgets.forEach(widget => {
    order[widget.section].push(widget.id);
  });
  return order;
};

/**
 * Get widgets by section
 */
export const getWidgetsBySection = (section) => {
  return widgets.filter(w => w.section === section);
};

/**
 * Get widget by ID
 */
export const getWidgetById = (id) => {
  return widgets.find(w => w.id === id);
};
